/**
 * Список Telegram-аккаунтов и переключатель режима (§5 ТЗ).
 *
 * Стажёр видит здесь «Account 01», «Account 02» — маскирование выполнено на
 * сервере, компонент просто отображает то, что пришло. Никакой логики вида
 * `if (role === 'INTERN') hide(...)` тут нет и быть не должно: она означала бы,
 * что настоящие данные всё-таки долетели до браузера.
 *
 * ── Группировка по командам ─────────────────────────────────────────────────
 *
 * Аккаунты сгруппированы по командам (папка второго уровня), команды — по
 * веткам. Нажатие на команду выбирает сразу все её аккаунты; нажатие на
 * отдельный аккаунт — только его. Так менеджер, ведущий команду, смотрит её
 * чаты целиком или по одному, не листая общий плоский список.
 */

import { useMemo, useState } from 'react';
import { MOBILE_QUERY } from '../lib/useMedia.ts';
import type { AnyTelegramAccountDto } from '@tw/shared-types';
import type { ConnectionStatus } from '../lib/ws.js';
import { accountLabel, accountSubtitle } from './accountLabel.js';
import { Avatar } from './Avatar.tsx';
import { ChevronIcon } from './icons.tsx';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn, formatCount } from '@/lib/utils';

/**
 * Свёрнутость панели переживает перезагрузку: у менеджера с одним аккаунтом
 * список бесполезен, и разворачивать его каждый раз заново — раздражает.
 */
const COLLAPSE_KEY = 'tw.accounts.collapsed';

/**
 * Выбор в списке: все аккаунты, одна команда целиком, либо один аккаунт.
 *
 * Строкой, а не объектом: значение уходит в фильтр inbox и хранится в URL-подобном
 * состоянии, и плоское значение сравнивается без глубокого равенства.
 */
export type AccountSelection =
  | { kind: 'all' }
  | { kind: 'team'; teamId: string; accountIds: string[] }
  | { kind: 'account'; accountId: string };

interface AccountSidebarProps {
  accounts: AnyTelegramAccountDto[];
  /** Текущий выбор. null трактуется как «все» — совместимо со старым кодом. */
  selected: AccountSelection | null;
  onSelect: (selection: AccountSelection) => void;
  connectionStatus: ConnectionStatus;
}

const STATE_DOT: Record<string, string> = {
  CONNECTED: 'bg-[var(--ok)]',
  RECONNECTING: 'bg-[var(--warn)]',
  AUTHORIZATION_REQUIRED: 'bg-[var(--warn)]',
  ERROR: 'bg-destructive',
  DISCONNECTED: 'bg-muted-foreground',
};

interface TeamGroup {
  id: string;
  name: string;
  branchName: string | null;
  accounts: AnyTelegramAccountDto[];
  unread: number;
}

export function AccountSidebar({
  accounts,
  selected,
  onSelect,
  connectionStatus,
}: AccountSidebarProps) {
  const totalUnread = accounts.reduce((sum, a) => sum + a.unreadCount, 0);

  /*
   * На телефоне — свой ключ и по умолчанию свёрнуто: развёрнутый список
   * аккаунтов занимал полэкрана, и до первого чата приходилось листать.
   */
  const [key] = useState(() =>
    globalThis.matchMedia?.(MOBILE_QUERY).matches ? `${COLLAPSE_KEY}.mobile` : COLLAPSE_KEY,
  );
  const [collapsed, setCollapsed] = useState(() => {
    const stored = globalThis.localStorage?.getItem(key);
    return key === COLLAPSE_KEY ? stored === '1' : stored !== '0';
  });
  /** Раскрытые команды. По умолчанию раскрыты все — свернуть можно вручную. */
  const [closedTeams, setClosedTeams] = useState<ReadonlySet<string>>(new Set());

  const toggle = (): void => {
    setCollapsed((prev) => {
      globalThis.localStorage?.setItem(key, prev ? '0' : '1');
      return !prev;
    });
  };

  /*
   * Разбивка на команды. Аккаунты без команды собираются в псевдогруппу
   * «Без команды» с пустым id — она всегда идёт последней. Команды
   * сортируются по ветке и имени, чтобы порядок был устойчивым.
   */
  const groups = useMemo<TeamGroup[]>(() => {
    const byTeam = new Map<string, TeamGroup>();
    for (const account of accounts) {
      const teamId = account.team?.id ?? '';
      const existing = byTeam.get(teamId);
      if (existing) {
        existing.accounts.push(account);
        existing.unread += account.unreadCount;
      } else {
        byTeam.set(teamId, {
          id: teamId,
          name: account.team?.name ?? 'Без команды',
          branchName: account.branch?.name ?? null,
          accounts: [account],
          unread: account.unreadCount,
        });
      }
    }
    return [...byTeam.values()].sort((a, b) => {
      if (a.id === '') return 1;
      if (b.id === '') return -1;
      return (
        (a.branchName ?? '').localeCompare(b.branchName ?? '', 'ru') ||
        a.name.localeCompare(b.name, 'ru')
      );
    });
  }, [accounts]);

  // Единственная команда без ветки — группировать не за чем: показываем плоско.
  const flat = groups.length === 1 && groups[0]?.id === '';

  const isAll = selected === null || selected.kind === 'all';

  return (
    <div className="flex-none border-b border-border p-2">
      <div className="flex items-center gap-2 px-1.5 pb-1.5 pt-1">
        <button
          type="button"
          onClick={toggle}
          aria-expanded={!collapsed}
          className="flex flex-1 items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.07em] text-muted-foreground transition-colors hover:text-foreground"
        >
          <ChevronIcon
            className={cn('size-4 transition-transform', collapsed && '-rotate-90')}
          />
          Аккаунты
          {collapsed && totalUnread > 0 && (
            <span className="ml-auto grid h-5 min-w-5 flex-none place-items-center rounded-full bg-primary px-1.5 text-[11px] font-semibold text-primary-foreground">
              {formatCount(totalUnread)}
            </span>
          )}
        </button>

        <Tooltip>
          <TooltipTrigger asChild>
            <span
              className={cn(
                'size-2 flex-none rounded-full',
                connectionStatus === 'open' && 'bg-[var(--ok)] ring-4 ring-[var(--ok)]/20',
                connectionStatus === 'connecting' && 'bg-[var(--warn)]',
                connectionStatus === 'closed' && 'bg-destructive',
              )}
            />
          </TooltipTrigger>
          <TooltipContent>
            {connectionStatus === 'open'
              ? 'Обновления приходят в реальном времени'
              : 'Соединение потеряно — данные могут быть неактуальны'}
          </TooltipContent>
        </Tooltip>
      </div>

      {!collapsed && (
        <div className="max-h-[42vh] overflow-y-auto">
          <button
            type="button"
            onClick={() => onSelect({ kind: 'all' })}
            className={cn(
              'flex w-full items-center gap-2 rounded-lg border-transparent bg-transparent px-2.5 py-1.5 text-left text-[13px] transition-colors',
              isAll ? 'bg-primary/10 font-medium' : 'hover:bg-accent',
            )}
          >
            Все аккаунты
            {totalUnread > 0 && (
              <span className="ml-auto grid h-5 min-w-5 flex-none place-items-center rounded-full bg-primary px-1.5 text-[11px] font-semibold text-primary-foreground">
                {formatCount(totalUnread)}
              </span>
            )}
          </button>

          <div className="my-1 h-px bg-border" />

          {accounts.length === 0 && (
            <p className="px-2.5 py-2 text-xs leading-relaxed text-muted-foreground">
              Вам не назначен ни один Telegram-аккаунт. Обратитесь к руководителю.
            </p>
          )}

          {groups.map((group) => {
            const teamSelected =
              selected?.kind === 'team' && selected.teamId === group.id;
            const open = !closedTeams.has(group.id);

            return (
              <div key={group.id || 'none'}>
                {/* Заголовок команды виден, только когда команд больше одной:
                    один плоский список не нуждается в шапке. */}
                {!flat && (
                  <div className="mt-0.5 flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() =>
                        setClosedTeams((prev) => {
                          const next = new Set(prev);
                          if (next.has(group.id)) next.delete(group.id);
                          else next.add(group.id);
                          return next;
                        })
                      }
                      aria-expanded={open}
                      className="grid size-6 flex-none place-items-center rounded-md border-transparent bg-transparent text-muted-foreground transition-colors hover:bg-accent"
                    >
                      <ChevronIcon
                        className={cn('size-3.5 transition-transform', !open && '-rotate-90')}
                      />
                    </button>
                    {/* Нажатие на имя команды — выбрать её аккаунты целиком. */}
                    <button
                      type="button"
                      onClick={() =>
                        onSelect({
                          kind: 'team',
                          teamId: group.id,
                          accountIds: group.accounts.map((a) => a.id),
                        })
                      }
                      className={cn(
                        'flex min-w-0 flex-1 items-center gap-1.5 rounded-md border-transparent bg-transparent px-1.5 py-1 text-left transition-colors',
                        teamSelected ? 'bg-primary/10' : 'hover:bg-accent',
                      )}
                    >
                      <span
                        className={cn(
                          'truncate text-[11px] font-semibold uppercase tracking-[0.05em]',
                          teamSelected ? 'text-brand' : 'text-muted-foreground',
                        )}
                      >
                        {group.branchName ? `${group.branchName} · ` : ''}
                        {group.name}
                      </span>
                      {group.unread > 0 && (
                        <span className="ml-auto grid h-4 min-w-4 flex-none place-items-center rounded-full bg-primary/80 px-1.5 text-[10px] font-semibold text-primary-foreground">
                          {formatCount(group.unread)}
                        </span>
                      )}
                    </button>
                  </div>
                )}

                {open &&
                  group.accounts.map((account) => {
                    const subtitle = accountSubtitle(account);
                    const active =
                      selected?.kind === 'account' && selected.accountId === account.id;
                    // Настоящий аватар — только для полного вида: маскированному
                    // стажёру он раскрыл бы, чей это аккаунт (сервер отдаст 404,
                    // но и запрашивать незачем).
                    const photoUrl = account.masked
                      ? null
                      : `/api/telegram/accounts/${account.id}/photo`;

                    return (
                      <button
                        key={account.id}
                        type="button"
                        onClick={() => onSelect({ kind: 'account', accountId: account.id })}
                        className={cn(
                          // Строка-«таблетка»: у активной — заливка бренда,
                          // у остальных мягкая подложка. Отдельная плашка на
                          // каждый аккаунт (а не голый ряд) не даёт им слипаться
                          // в один список, когда у всех одинаковый аватар.
                          'mt-1 flex w-full items-center gap-2.5 rounded-xl border border-transparent py-2 text-left transition-colors',
                          flat ? 'px-2.5' : 'ml-6 pl-2 pr-2.5',
                          active
                            ? 'bg-primary text-primary-foreground shadow-[0_2px_10px_-4px_var(--accent-fill)]'
                            : 'bg-secondary/60 hover:bg-accent',
                        )}
                      >
                        <span className="relative flex-none">
                          <Avatar
                            name={accountLabel(account)}
                            seed={account.id}
                            size="xs"
                            photoUrl={photoUrl}
                          />
                          <span
                            className={cn(
                              'absolute -bottom-px -right-px size-2.5 rounded-full border-2 border-card',
                              STATE_DOT[account.connectionState] ?? 'bg-muted-foreground',
                            )}
                            title={account.connectionState}
                          />
                        </span>

                        <span className="flex min-w-0 flex-col leading-tight">
                          <span className="truncate text-[13px] font-medium">
                            {accountLabel(account)}
                          </span>
                          {subtitle && (
                            <span
                              className={cn(
                                'truncate text-[11px]',
                                active ? 'text-primary-foreground/75' : 'text-muted-foreground',
                              )}
                            >
                              {subtitle}
                            </span>
                          )}
                        </span>

                        {account.unreadCount > 0 && (
                          <span
                            className={cn(
                              'ml-auto grid h-5 min-w-5 flex-none place-items-center rounded-full px-1.5 text-[11px] font-semibold',
                              active
                                ? 'bg-primary-foreground text-primary'
                                : 'bg-primary text-primary-foreground',
                            )}
                          >
                            {formatCount(account.unreadCount)}
                          </span>
                        )}
                      </button>
                    );
                  })}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
